/*
 * Hero ad banners. A JS clock owns the timing and remembers the time left when paused; the
 * product-row progress line (CSS animation) pauses with it, so the two stay in step.
 * Pause reasons: keyboard focus, finger on the banner, hidden tab, pause button, theme editor (not mouse hover).
 */
(function () {
  function init(hero) {
    if (hero.dataset.ready) return;
    hero.dataset.ready = '1';
    const slides = [...hero.querySelectorAll('.hb__slide')];
    const thumbs = [...hero.querySelectorAll('[data-hb-thumbs] button')];
    const track = hero.querySelector('[data-hb-track]');
    const pauseBtn = hero.querySelector('[data-hb-pause]');
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const interval = +hero.dataset.interval || 5000;
    const strings = (window.Sansu && Sansu.strings) || {};
    hero.style.setProperty('--hb-cycle', interval + 'ms');
    let i = 0;

    // Only the first banner's photo loads with the page. After that each banner is fetched just before it is
    // needed (the next one, and the one a visitor taps), instead of all six at once: a lighter first load,
    // and nothing is downloaded for banners nobody gets to see.
    const loadSlide = el => el && el.querySelectorAll('[data-src], [data-srcset]').forEach(img => {
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      if (img.dataset.src) img.src = img.dataset.src;
      img.removeAttribute('data-src'); img.removeAttribute('data-srcset');
    });
    const loadAround = n => { loadSlide(slides[(n + 1) % slides.length]); loadSlide(slides[(n + 2) % slides.length]); };
    document.readyState === 'complete' ? loadAround(0) : addEventListener('load', () => loadAround(0));
    if (slides.length < 2) return;

    // The "held" bottle is placed in the photo's own coordinates: where the person's hand is on the original photo,
    // translated through the way the photo is cropped on this screen (object-fit: cover + object-position), and
    // mirrored when the slide's photo is flipped. Run on load, resize and whenever a banner is shown.
    function placeHeld(slide) {
      const held = slide.querySelector('[data-held]'), img = slide.querySelector('.hb__img'), bg = slide.querySelector('.hb__bg');
      if (!held || !img || !bg || !img.naturalWidth || !img.offsetWidth) return;
      const cw = img.offsetWidth, ch = img.offsetHeight, iw = img.naturalWidth, ih = img.naturalHeight;
      const scale = Math.max(cw / iw, ch / ih), dw = iw * scale, dh = ih * scale;
      const pos = getComputedStyle(img).objectPosition.split(' ').map(v => parseFloat(v) / 100);
      const ox = img.offsetLeft + (cw - dw) * (isNaN(pos[0]) ? .5 : pos[0]), oy = img.offsetTop + (ch - dh) * (isNaN(pos[1]) ? .5 : pos[1]);
      let fx = (+held.dataset.x || 50) / 100; const fy = (+held.dataset.y || 60) / 100;
      if (slide.classList.contains('hb__slide--mirror')) fx = 1 - fx;
      const cx = ox + fx * dw, cy = oy + fy * dh, hh = ((+held.dataset.h || 40) / 100) * dh * (innerWidth < 900 ? 1.22 : 1); // a little larger on phones
      bg.style.setProperty('--cx', cx.toFixed(1) + 'px'); bg.style.setProperty('--cy', cy.toFixed(1) + 'px'); bg.style.setProperty('--hh', hh.toFixed(1) + 'px');
      held.style.setProperty('--ht', (+held.dataset.tilt || 0) + 'deg');
      held.dataset.side = cx > bg.clientWidth * .6 ? 'left' : 'right'; // the tag opens towards the middle of the photo
      bg.classList.add('has-held');
    }
    const placeAll = () => slides.forEach(placeHeld);
    slides.forEach(s => { const im = s.querySelector('.hb__img'); if (im) im.addEventListener('load', () => placeHeld(s)); });
    let rz; addEventListener('resize', () => { cancelAnimationFrame(rz); rz = requestAnimationFrame(placeAll); });
    addEventListener('load', placeAll); placeAll();

    let prevTimer;
    function show(n, manual) {
      const prev = i;
      i = (n + slides.length) % slides.length;
      loadSlide(slides[i]); loadAround(i); placeHeld(slides[i]);
      if (prev !== i) {
        // the outgoing photo stays underneath while the new one fades in over it
        hero.classList.add('is-started');
        slides.forEach(s => s.classList.remove('is-prev'));
        slides[prev].classList.add('is-prev');
        clearTimeout(prevTimer);
        prevTimer = setTimeout(() => slides[prev].classList.remove('is-prev'), 700);
        slides[prev].classList.remove('is-active');
        slides[prev].setAttribute('aria-hidden', 'true'); slides[prev].inert = true;
        slides[i].classList.add('is-active');
        slides[i].removeAttribute('aria-hidden'); slides[i].inert = false;
      }
      thumbs.forEach((t, k) => k === i ? t.setAttribute('aria-current', 'true') : t.removeAttribute('aria-current'));
      // announce only when the visitor chose the banner, not on every automatic change
      track.setAttribute('aria-live', manual ? 'polite' : 'off');
    }

    if (reduce) { // no automatic movement; the product row still switches banners
      hero.classList.add('is-static');
      thumbs.forEach((t, n) => t.addEventListener('click', () => show(n, true)));
      if (pauseBtn) pauseBtn.hidden = true;
      swipe(n => show(n, true));
      return;
    }

    const reasons = new Set();
    let timer = null, left = interval, startedAt = 0;
    const restartFill = () => { const t = thumbs[i]; if (t) { t.removeAttribute('aria-current'); void t.offsetWidth; t.setAttribute('aria-current', 'true'); } };
    // With a short interval the next photo may still be downloading: wait for it rather than show an empty banner
    const ready = s => { loadSlide(s); return [...s.querySelectorAll('.hb__bg img, .hs__poster')].every(img => img.complete && (img.naturalWidth > 0 || !img.currentSrc)); };
    function tick() {
      timer = null;
      const next = slides[(i + 1) % slides.length];
      if (!ready(next)) { left = 200; return run(); }
      left = interval; show(i + 1); run();
    }
    function run() {
      if (reasons.size || timer) return;
      startedAt = performance.now();
      timer = setTimeout(tick, left);
    }
    function halt() {
      if (!timer) return;
      clearTimeout(timer); timer = null;
      left = Math.max(0, left - (performance.now() - startedAt));
    }
    const sync = () => { hero.classList.toggle('is-paused', reasons.size > 0); reasons.size ? halt() : run(); };
    const pause = r => { reasons.add(r); sync(); };
    const resume = r => { reasons.delete(r); sync(); };
    const go = n => { halt(); left = interval; show(n, true); restartFill(); run(); };

    thumbs.forEach((t, n) => t.addEventListener('click', () => go(n)));
    swipe(go);

    // No pause on mouse hover over the banner: it keeps moving while the cursor passes over it.
    // Only while the cursor is on the buy buttons or pack card does it hold, so a click can't land on the next product.
    const buyZone = '.hb__ctas, .hb__pack, .hb__held';
    hero.addEventListener('mouseover', e => { if (e.target.closest(buyZone)) pause('buy'); });
    hero.addEventListener('mouseout', e => {
      if (e.target.closest(buyZone) && !(e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest(buyZone))) resume('buy');
    });
    hero.addEventListener('touchstart', () => pause('touch'), { passive: true });
    hero.addEventListener('touchend', () => resume('touch'));
    hero.addEventListener('touchcancel', () => resume('touch'));
    hero.addEventListener('focusin', e => { if (e.target.matches(':focus-visible') && e.target !== pauseBtn) pause('focus'); });
    hero.addEventListener('focusout', e => { if (!hero.contains(e.relatedTarget)) resume('focus'); });
    document.addEventListener('visibilitychange', () => document.hidden ? pause('hidden') : resume('hidden'));

    pauseBtn.addEventListener('click', () => {
      const paused = !reasons.has('user');
      paused ? pause('user') : resume('user');
      hero.classList.toggle('is-user-paused', paused);
      pauseBtn.setAttribute('aria-pressed', paused);
      pauseBtn.setAttribute('aria-label', paused ? strings.play : strings.pause);
    });

    // Theme editor: selecting a banner block shows it and holds the rotation.
    document.addEventListener('shopify:block:select', e => {
      const n = slides.findIndex(s => s.dataset.blockId === e.detail.blockId);
      if (n > -1) { pause('editor'); show(n, true); }
    });
    document.addEventListener('shopify:block:deselect', () => resume('editor'));

    pause('loading');
    const start = () => { restartFill(); resume('loading'); };
    document.readyState === 'complete' ? start() : addEventListener('load', start);

    // Horizontal swipe on the banner (vertical scrolling is left alone).
    function swipe(to) {
      let x0 = null, y0 = 0;
      track.addEventListener('touchstart', e => { x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; }, { passive: true });
      track.addEventListener('touchend', e => {
        if (x0 === null) return;
        const dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
        x0 = null;
        if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.4) to(i + (dx < 0 ? 1 : -1));
      });
    }
  }

  document.querySelectorAll('[data-hb]').forEach(init);
  document.addEventListener('shopify:section:load', e => e.target.querySelectorAll('[data-hb]').forEach(init));
})();
